/* eslint-disable no-console */
import { batch } from 'react-redux';

import { handleErrors } from './ErrorActions';
import { addSuccess } from './SuccessActions';
import { clearWarnings, enableWarnings, updateAllWarnings } from './WarningActions';
import {
  deleteDocumentRepositoryConfig,
  publishDocumentRepositoryConfig,
  saveDistribution,
  saveDocumentRepositoryConfig,
  saveDocumentRepositoryConfigFromTemplate,
  unpublishDocumentRepositoryConfig,
} from '../api/documentRepositoryConfiguration';
import {
  addValidationErrors,
  deleteValidationErrors,
  loadSavedSearches,
} from '../saved-search-designer/actions/editor.actions';
import { validateSavedSearchOnSave } from '../saved-search-designer/utils/validation.utils';
import {
  getExistingRepositoryConfigData,
  modifyRepositoryNodesForSave,
  removeKeyFromTree,
  validateDistributionOnSave,
  validateRepositoryOnSave,
} from '../utils/repositoryConfigurationUtils';

export const SET_REPOSITORY_DATA = 'SET_REPOSITORY_DATA';
export const UPDATE_REPOSITORY_TREE = 'UPDATE_REPOSITORY_TREE';
export const SET_DISTRIBUTION_MODAL_OPEN = 'SET_DISTRIBUTION_MODAL_OPEN';
export const CLEAR_REPOSITORY_CONFIGURATION = 'CLEAR_REPOSITORY_CONFIGURATION';
export const SET_REPOSITORY_PROP_VALUE = 'SET_REPOSITORY_PROP_VALUE';
export const UPDATE_ORIGINAL_REPOSITORY_PROP_VALUE = 'UPDATE_ORIGINAL_REPOSITORY_PROP_VALUE';

export const ADD_DISTRIBUTION_LIST_ROWS = '[REPOSITORY]ADD_DISTRIBUTION_LIST_ROWS';
export const DELETE_DISTRIBUTION_LIST_ROW = '[REPOSITORY]DELETE_DISTRIBUTION_LIST_ROW';

export const setRepositoryData = (repositoryData) => ({
  type: SET_REPOSITORY_DATA,
  payload: { repositoryData },
});

export const clearRepositoryConfiguration = () => ({
  type: CLEAR_REPOSITORY_CONFIGURATION,
});

export const updateRepositoryTree = (newTree) => ({
  type: UPDATE_REPOSITORY_TREE,
  payload: { newTree },
});

export const setDistributionModalOpen = (flag) => ({
  type: SET_DISTRIBUTION_MODAL_OPEN,
  payload: { flag },
});

export const setRepositoryPropValue = (data) => ({
  type: SET_REPOSITORY_PROP_VALUE,
  payload: data,
});

export const updateOriginalRepositoryPropValue = (data) => ({
  type: UPDATE_ORIGINAL_REPOSITORY_PROP_VALUE,
  payload: data,
});

export const addDistributionListRows = (data) => ({
  type: ADD_DISTRIBUTION_LIST_ROWS,
  payload: data,
});

export const deleteDistributionListRow = (data) => ({
  type: DELETE_DISTRIBUTION_LIST_ROW,
  payload: data,
});

export const validateRepository = ({ validateSearchName = false } = {}) => async (
  dispatch,
  getState
) => {
  try {
    const { repositoryDetails } = getState().repositoryConfiguration;
    const { savedSearches, isDocRepository } = getState().savedSearchDesigner;
    const translations = getState().translations;

    dispatch(clearWarnings());
    dispatch(deleteValidationErrors());

    const [repositoryValErrors, savedSearchesValError] = await Promise.all([
      validateRepositoryOnSave({
        repositoryDetails,
        translations,
        validateSearchName,
      }),
      validateSavedSearchOnSave({
        translations,
        savedSearches,
        isDocRepository,
      }),
    ]);

    if (repositoryValErrors.length || savedSearchesValError.length) {
      dispatch(updateAllWarnings([...repositoryValErrors, ...savedSearchesValError]));
      dispatch(addValidationErrors(savedSearchesValError));
      dispatch(enableWarnings());
    }

    return !repositoryValErrors.length && !savedSearchesValError.length;
  } catch (error) {
    console.error(`validateRepository() failed: ${error}`);
  }
};

export const saveRepositoryConfig = ({
  setIsLoading,
  newRepositoryName,
  redirectToRepository,
  saveAsNew = false,
}) => async (dispatch, getState) => {
  try {
    setIsLoading(true);
    const isValid = await dispatch(validateRepository({ validateSearchName: true }));

    if (!isValid) {
      setIsLoading(false);
      return;
    }

    const state = getState();
    const { repositoryId, type, adminDistribution, viewDistribution, name, repositoryTree } =
      state.repositoryConfiguration.repositoryDetails || {};
    const { savedSearches: savedSearchesStore, entityFields } = state.savedSearchDesigner;

    const nodes = modifyRepositoryNodesForSave(savedSearchesStore, entityFields);
    const preprocessedTree = removeKeyFromTree(repositoryTree, 'expanded');

    const payloadObj = {
      repositoryId,
      name: saveAsNew ? newRepositoryName : name,
      saveAsNew,
      type,
      repositoryTree: preprocessedTree,
      nodes,
      viewDistribution,
      adminDistribution,
    };

    const response = await saveDocumentRepositoryConfig(payloadObj);

    setIsLoading(false);

    if (response.hasError) {
      dispatch(handleErrors(response.errors));
      return;
    }

    const {
      savedSearches,
      repositoryDetails,
      selectedSavedSearchId,
    } = getExistingRepositoryConfigData(response.RepositoryDetails, response.repositoryId);

    delete repositoryDetails.nodes;

    batch(() => {
      dispatch(addSuccess({ message: 'Document Repository saved successfully' }));
      dispatch(loadSavedSearches({ savedSearches, selectedSavedSearchId, isDocRepository: true }));
      dispatch(setRepositoryData(repositoryDetails));
    });

    redirectToRepository({ repositoryId: response.repositoryId });
  } catch (error) {
    console.error(`saveRepositoryConfig() failed: ${error}`);
    setIsLoading(false);
  }
};

export const createRepositoryFromTemplate = ({
  setIsLoading,
  repositoryName,
  product,
  redirectToRepository,
}) => async (dispatch, getState) => {
  try {
    const isValid = await dispatch(validateRepository());

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    const state = getState();
    const { repositoryId, type, adminDistribution, viewDistribution, repositoryTree } =
      state.repositoryConfiguration.repositoryDetails || {};
    const { savedSearches: savedSearchesStore, entityFields } = state.savedSearchDesigner;

    const nodes = modifyRepositoryNodesForSave(savedSearchesStore, entityFields);
    const preprocessedTree = removeKeyFromTree(repositoryTree, 'expanded');

    const payloadObj = {
      repositoryId,
      name: repositoryName,
      productName: product,
      type,
      repositoryTree: preprocessedTree,
      nodes,
      viewDistribution,
      adminDistribution,
    };

    const response = await saveDocumentRepositoryConfigFromTemplate(payloadObj);

    setIsLoading(false);

    if (response.hasError) {
      dispatch(handleErrors(response.errors));
      return;
    }

    const {
      savedSearches,
      repositoryDetails,
      selectedSavedSearchId,
    } = getExistingRepositoryConfigData(response.RepositoryDetails, response.repositoryId);

    delete repositoryDetails.nodes;

    batch(() => {
      dispatch(addSuccess({ message: 'Document Repository created successfully' }));
      dispatch(loadSavedSearches({ savedSearches, selectedSavedSearchId, isDocRepository: true }));
      dispatch(setRepositoryData(repositoryDetails));
    });

    redirectToRepository({ repositoryId: response.repositoryId });
  } catch (error) {
    console.error(`createRepositoryFromTemplate() failed: ${error}`);
    setIsLoading(false);
  }
};

export const deleteRepositoryConfig = ({ redirectToPrevPage }) => async (dispatch, getState) => {
  try {
    const { repositoryId } = getState().repositoryConfiguration.repositoryDetails || {};

    const response = await deleteDocumentRepositoryConfig({ repositoryId });

    if (response.hasError) {
      dispatch(handleErrors(response.errors));
      return;
    }

    dispatch(addSuccess({ message: 'Document Repository deleted successfully' }));
    redirectToPrevPage();
  } catch (error) {
    throw new Error(`deleteRepositoryConfig() failed: ${error}`);
  }
};

export const publishRepositoryConfig = ({
  comment,
  password,
  redirectToPrevPage,
  setIsLoading,
}) => async (dispatch, getState) => {
  try {
    const state = getState();
    const { repositoryId, type, adminDistribution, viewDistribution, name, repositoryTree } =
      state.repositoryConfiguration.repositoryDetails || {};
    const { savedSearches, entityFields } = state.savedSearchDesigner;

    const nodes = modifyRepositoryNodesForSave(savedSearches, entityFields);
    const preprocessedTree = removeKeyFromTree(repositoryTree, 'expanded');

    const payloadObj = {
      repositoryId,
      name,
      type,
      repositoryTree: preprocessedTree,
      nodes,
      viewDistribution,
      adminDistribution,
      comment,
      password,
    };

    const response = await publishDocumentRepositoryConfig(payloadObj);

    setIsLoading(false);

    if (response.hasError) {
      dispatch(handleErrors(response.errors));
      return;
    }

    dispatch(addSuccess({ message: 'Document Repository published successfully' }));
    redirectToPrevPage();
  } catch (error) {
    console.error(`publishRepositoryConfig() failed: ${error}`);
    setIsLoading(false);
  }
};

export const unpublishRepositoryConfig = ({
  comment = '',
  password,
  redirectToPrevPage,
  setIsLoading,
}) => async (dispatch, getState) => {
  try {
    const state = getState();
    const { repositoryId } = state.repositoryConfiguration.repositoryDetails || {};

    const payloadObj = {
      repositoryId,
      comment,
      password,
    };

    const response = await unpublishDocumentRepositoryConfig(payloadObj);

    setIsLoading(false);

    if (response.hasError) {
      dispatch(handleErrors(response.errors));
      return;
    }

    dispatch(addSuccess({ message: 'Document Repository unpublished successfully' }));
    redirectToPrevPage();
  } catch (error) {
    console.error(`unpublishRepositoryConfig() failed: ${error}`);
    setIsLoading(false);
  }
};

export const saveRepositoryDistributionConfig = (setIsLoading) => async (dispatch, getState) => {
  const { translations, repositoryConfiguration } = getState();
  const { repositoryDetails } = repositoryConfiguration;

  try {
    dispatch(clearWarnings());

    const validationErrors = validateDistributionOnSave({ translations, repositoryDetails });

    if (validationErrors.length) {
      dispatch(updateAllWarnings(validationErrors));
      dispatch(enableWarnings());
      return;
    }

    const { viewDistribution, adminDistribution, repositoryId } = repositoryDetails;

    setIsLoading(true);

    const response = await saveDistribution({ viewDistribution, adminDistribution, repositoryId });

    setIsLoading(false);

    if (response.hasError) {
      dispatch(handleErrors(response.errors));
      return;
    }

    batch(() => {
      dispatch(
        updateOriginalRepositoryPropValue({
          prop: 'viewDistribution',
          value: repositoryDetails.viewDistribution,
        })
      );
      dispatch(
        updateOriginalRepositoryPropValue({
          prop: 'adminDistribution',
          value: repositoryDetails.adminDistribution,
        })
      );
      dispatch(setDistributionModalOpen(false));
      dispatch(addSuccess({ message: 'Distribution saved successfully' }));
    });
  } catch (error) {
    console.error(`saveRepositoryDistributionConfig() failed: ${error}`);
    setIsLoading(false);
  }
};

export const revertDistributionToOriginal = (dispatch, getState) => {
  const { originalRepositoryDetails } = getState().repositoryConfiguration;

  batch(() => {
    dispatch(
      setRepositoryPropValue({
        prop: 'viewDistribution',
        value: originalRepositoryDetails.viewDistribution,
      })
    );
    dispatch(
      setRepositoryPropValue({
        prop: 'adminDistribution',
        value: originalRepositoryDetails.adminDistribution,
      })
    );
  });
};
